import React from 'react';
import { Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatters';
import type { MembershipPlan, CardToken } from '../../hooks/usePayments';

interface PaymentMethodSelectorProps {
  /** Whether OnePay is enabled */
  enabled: boolean;
  /** Currently selected payment route */
  paymentRoute: 'vietqr' | 'card';
  /** Set payment route */
  setPaymentRoute: (route: 'vietqr' | 'card') => void;
  /** Whether to save card for future use */
  saveCard: boolean;
  /** Set save card */
  setSaveCard: (save: boolean) => void;
  /** Selected saved card token */
  selectedToken: CardToken | null;
  /** Set selected token */
  setSelectedToken: (token: CardToken | null) => void;
  /** Saved cards list */
  savedCards: CardToken[] | undefined;
  /** Whether OnePay payment is processing */
  isProcessing: boolean;
  /** Selected plan ID for payment */
  planId: string | null;
  /** Available plans for price lookup */
  plans: MembershipPlan[] | undefined;
  /** Handle OnePay payment */
  onPay: () => void;
  /** Callback URL for OnePay */
  callbackUrl: string;
}

function toOnePayLocale(lang: string): string {
  return lang === 'vi' ? 'vn' : 'en';
}

function cardBrandIcon(brand: string): string {
  switch (brand) {
    case 'visa': return 'credit-card';
    case 'mastercard': return 'credit-card';
    case 'amex': return 'credit-card';
    case 'jcb': return 'credit-card';
    default: return 'credit-card-outline';
  }
}

export default function PaymentMethodSelector({
  enabled,
  paymentRoute,
  setPaymentRoute,
  saveCard,
  setSaveCard,
  selectedToken,
  setSelectedToken,
  savedCards,
  isProcessing,
  planId,
  plans,
  onPay,
  callbackUrl,
}: PaymentMethodSelectorProps) {
  const { t } = useTranslation();
  const colors = useAppColors();

  if (!enabled) return null;

  return (
    <>
      {/* Payment Method Selection */}
      <Text style={[styles.paymentTitle, { color: colors.text }]}>{t('billing.paymentMethod')}</Text>

      {/* VietQR Push Payment */}
      <TouchableOpacity
        style={[
          styles.payOption,
          {
            backgroundColor: colors.bg,
            borderColor: paymentRoute === 'vietqr' ? colors.text : colors.border,
          },
        ]}
        activeOpacity={0.7}
        onPress={() => { setPaymentRoute('vietqr'); setSelectedToken(null); }}
      >
        <View style={[styles.radioOuter, { borderColor: paymentRoute === 'vietqr' ? colors.text : colors.textTertiary }]}>
          {paymentRoute === 'vietqr' && <View style={[styles.radioDot, { backgroundColor: colors.text }]} />}
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <View style={styles.payOptionRow}>
            <View style={{ flex: 1 }}>
              <View style={styles.payOptionTitleRow}>
                <Icon name="qrcode-scan" size={16} color={colors.textSecondary} />
                <Text style={[styles.payOptionTitle, { color: colors.text }]}>{t('subscription.vietqrPush')}</Text>
              </View>
              <Text style={[styles.payOptionSub, { color: colors.textTertiary }]}>{t('subscription.vietqrPushSub')}</Text>
            </View>
            <View style={[styles.payBadge, { backgroundColor: colors.border }]}>
              <Text style={[styles.payBadgeText, { color: colors.textSecondary }]}>VietQR</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>

      {/* International Card */}
      <TouchableOpacity
        style={[
          styles.payOption,
          {
            backgroundColor: colors.bg,
            borderColor: paymentRoute === 'card' && !selectedToken ? colors.text : colors.border,
          },
        ]}
        activeOpacity={0.7}
        onPress={() => { setPaymentRoute('card'); setSelectedToken(null); }}
      >
        <View style={[styles.radioOuter, { borderColor: paymentRoute === 'card' && !selectedToken ? colors.text : colors.textTertiary }]}>
          {paymentRoute === 'card' && !selectedToken && <View style={[styles.radioDot, { backgroundColor: colors.text }]} />}
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <View style={styles.payOptionRow}>
            <View style={{ flex: 1 }}>
              <View style={styles.payOptionTitleRow}>
                <Icon name="credit-card-outline" size={16} color={colors.textSecondary} />
                <Text style={[styles.payOptionTitle, { color: colors.text }]}>{t('billing.internationalCard')}</Text>
              </View>
              <Text style={[styles.payOptionSub, { color: colors.textTertiary }]}>{t('billing.internationalCardSub')}</Text>
            </View>
            <View style={[styles.payBadge, { backgroundColor: colors.border }]}>
              <Text style={[styles.payBadgeText, { color: colors.textSecondary }]}>OnePay</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>

      {/* Saved Cards */}
      {savedCards && savedCards.length > 0 && (
        <>
          <Text style={[styles.savedCardsLabel, { color: colors.textTertiary }]}>{t('billing.savedCards')}</Text>
          {savedCards.map((card) => (
            <TouchableOpacity
              key={card.id}
              style={[
                styles.payOption,
                {
                  backgroundColor: colors.bg,
                  borderColor: selectedToken?.id === card.id ? colors.text : colors.border,
                },
              ]}
              activeOpacity={0.7}
              onPress={() => { setPaymentRoute('card'); setSelectedToken(card); }}
            >
              <View style={[styles.radioOuter, { borderColor: selectedToken?.id === card.id ? colors.text : colors.textTertiary }]}>
                {selectedToken?.id === card.id && <View style={[styles.radioDot, { backgroundColor: colors.text }]} />}
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <View style={styles.payOptionRow}>
                  <View style={{ flex: 1 }}>
                    <View style={styles.payOptionTitleRow}>
                      <Icon name={cardBrandIcon(card.card_brand)} size={16} color={colors.textSecondary} />
                      <Text style={[styles.payOptionTitle, { color: colors.text }]}>{card.label || `****${card.last_four}`}</Text>
                    </View>
                    <Text style={[styles.payOptionSub, { color: colors.textTertiary }]}>{t('billing.recurringTokenized')}</Text>
                  </View>
                  <View style={[styles.payBadge, { backgroundColor: colors.border }]}>
                    <Text style={[styles.payBadgeText, { color: colors.textSecondary }]}>{t('billing.tokenBadge')}</Text>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </>
      )}

      {/* OnePay Card Panel */}
      {paymentRoute === 'card' ? (
        <View style={[styles.payPanel, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.cardPanelHeader}>
            <Text style={[styles.cardPanelTitle, { color: colors.text }]}>{t('billing.cardDetails')}</Text>
            <View style={styles.cardBrands}>
              <Icon name="credit-card" size={20} color={colors.textTertiary} />
            </View>
          </View>

          {selectedToken ? (
            /* Paying with saved token */
            <View style={styles.tokenInfo}>
              <Icon name={cardBrandIcon(selectedToken.card_brand)} size={28} color={colors.text} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={[styles.tokenLabel, { color: colors.text }]}>
                  {selectedToken.label || `****${selectedToken.last_four}`}
                </Text>
                <Text style={[styles.tokenSub, { color: colors.textTertiary }]}>
                  {t('billing.savedCardTokenized')}
                </Text>
              </View>
              <Icon name="check-circle" size={20} color={colors.success} />
            </View>
          ) : (
            /* First-time card — redirect info */
            <>
              <View style={[styles.redirectNotice, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                <Icon name="open-in-new" size={18} color={colors.textSecondary} style={{ marginTop: 1 }} />
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={[styles.noticeTitle, { color: colors.text }]}>{t('billing.secureRedirect')}</Text>
                  <Text style={[styles.noticeBody, { color: colors.textSecondary }]}>
                    {t('billing.secureRedirectDesc')}
                  </Text>
                </View>
              </View>

              {/* Tokenization checkbox */}
              <Pressable
                style={[styles.tokenCheckRow, { backgroundColor: colors.bg, borderColor: colors.border }]}
                onPress={() => setSaveCard(!saveCard)}
              >
                <Icon
                  name={saveCard ? 'checkbox-marked' : 'checkbox-blank-outline'}
                  size={20}
                  color={saveCard ? colors.text : colors.textTertiary}
                />
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={[styles.tokenCheckTitle, { color: colors.text }]}>
                    {t('billing.saveCardLabel')}
                  </Text>
                  <Text style={[styles.tokenCheckBody, { color: colors.textSecondary }]}>
                    {t('billing.saveCardDesc')}
                  </Text>
                </View>
              </Pressable>

              {/* OnePay Pay Button */}
              <TouchableOpacity
                style={[styles.payBtn, { backgroundColor: colors.accent, opacity: isProcessing ? 0.6 : 1 }]}
                activeOpacity={0.8}
                onPress={onPay}
                disabled={isProcessing || !planId}
              >
                <Text style={[styles.payBtnText, { color: colors.accentText }]}>
                  {isProcessing ? t('billing.processing') : t('billing.payAmount', { amount: formatCurrency(parseFloat(plans?.find((p: MembershipPlan) => p.id === planId)?.price || '0')) })}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  paymentTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 12,
  },
  payOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1.5,
    marginBottom: 10,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  payOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  payOptionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  payOptionTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  payOptionSub: {
    fontSize: 12,
  },
  payBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  payBadgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  savedCardsLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 8,
  },
  payPanel: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginTop: 8,
  },
  cardPanelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardPanelTitle: {
    fontSize: 15,
    fontWeight: '600',
  },
  cardBrands: {
    flexDirection: 'row',
    gap: 8,
  },
  tokenInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    backgroundColor: 'rgba(34,197,94,0.08)',
  },
  tokenLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  tokenSub: {
    fontSize: 12,
    marginTop: 2,
  },
  redirectNotice: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  noticeTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  noticeBody: {
    fontSize: 12,
    marginTop: 2,
    lineHeight: 16,
  },
  tokenCheckRow: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
  },
  tokenCheckTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  tokenCheckBody: {
    fontSize: 12,
    marginTop: 2,
  },
  payBtn: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  payBtnText: {
    fontSize: 15,
    fontWeight: '600',
  },
});
